import { useEffect, useRef } from "react";
import { showToastMsg } from "../generic-components/messages/CvpToastMessages";
import { useLocStore } from "../localization";
import { LocContext } from "../localization/LocContext";
import { useWhoAmI } from "./useWhoAmI";

/**
 * Shows a one-time toast when the user has connected a new Keycloak login
 * method (identity provider) since their previous session. WhoAmI carries
 * `newlyLinkedIdentityProviders` only on the first call of a session, so this
 * lives at the app root (rather than a single page) to fire wherever the user
 * lands first. Renders nothing.
 */
export const NewlyLinkedIdpToast = () => {
  const L = useLocStore(LocContext.PROFILE);
  const { whoAmI } = useWhoAmI();
  const newly = whoAmI?.newlyLinkedIdentityProviders;

  // Translations resolve to "" until the loc context has loaded, and WhoAmI can
  // arrive first — so wait for both. useLocStore re-renders this component when
  // the locale loads, which re-runs the effect. The ref keeps it to one toast.
  const translationsReady = !!L.gotTranslationsForLocale;
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!translationsReady) return;
    if (!newly || newly.length === 0) return;

    firedRef.current = true;
    const names = newly.map((p) =>
      // The backend marks the local email+password credential with this sentinel
      // display name; everything else uses Keycloak's own provider display name.
      p.displayName === "email+password"
        ? L.id("a1d6f0934c72").loc("E-post og passord")
        : p.displayName || p.providerName || p.providerAlias || ""
    );
    showToastMsg(
      L.id("b7f3c1029e84").split("Ny innloggingsmetode koblet til kontoen din: {0}", [
        names.join(", "),
      ]),
      "success"
    );
  }, [translationsReady, newly]);

  return null;
};
